import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, ShieldCheck, Wrench, ExternalLink, Search, Zap, BadgeCheck, Info, Clock3, XCircle } from "lucide-react";
import api, { apiError, inr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useLanguage, LANGS } from "@/context/LanguageContext";

function PaymentNote() {
  const { t } = useLanguage();
  return (
    <div className="flex items-center gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid="apply-payment-note">
      <ShieldCheck className="w-4 h-4 shrink-0" />
      {t("ap_payment_note")}
    </div>
  );
}

export default function ApplyCampaign() {
  const { code } = useParams();
  const [sp] = useSearchParams();
  const [state, setState] = useState(null);
  const [mobile, setMobile] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [pincode, setPincode] = useState("");
  const [consent, setConsent] = useState(false);
  const [showSlab, setShowSlab] = useState(false);
  const [loading, setLoading] = useState(false);
  const [checkMob, setCheckMob] = useState("");
  const [checkRes, setCheckRes] = useState(null);
  const [formResp, setFormResp] = useState({});
  const [done, setDone] = useState(false);
  const { t, lang, setLang } = useLanguage();

  const attribution = {
    campaign_id: code, partner_id: sp.get("partner_id"), sub_publisher_id: sp.get("sub_publisher_id"),
    user_id: sp.get("user_id"), click_id: sp.get("click_id"),
    utm_source: sp.get("utm_source") || "", utm_medium: sp.get("utm_medium") || "",
    utm_campaign: sp.get("utm_campaign") || "", utm_content: sp.get("utm_content") || "", utm_term: sp.get("utm_term") || "",
    referrer: typeof document !== "undefined" ? document.referrer : "", landing_page: typeof window !== "undefined" ? window.location.href : "",
  };

  useEffect(() => {
    api.get(`/public/campaign/${code}`).then((r) => {
      setState(r.data);
      if (r.data.available) api.post("/track/click", attribution).catch(() => {});
    }).catch(() => setState({ available: false, message: "Campaign not found." }));
    // eslint-disable-next-line
  }, [code]);

  const applyRedirect = async (e) => {
    e?.preventDefault();
    if (!consent) { toast.error(t("ap_consent_toast")); return; }
    setLoading(true);
    try {
      if (email && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) { toast.error("Enter a valid email address."); setLoading(false); return; }
      const { data } = await api.post("/apply/redirect", { ...attribution, mobile, full_name: name, email, pincode, consent });
      if (data.redirect_url) {
        toast.success("Redirecting to the application page…");
        window.location.href = data.redirect_url;
      } else {
        toast.error("Application link not configured yet. Please contact support.");
      }
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const submitDynamic = async (e) => {
    e?.preventDefault();
    if (!consent) { toast.error(t("ap_consent_toast")); return; }
    setLoading(true);
    const fields = state.form.fields || [];
    for (const f of fields) {
      if (f.type === "hidden") continue;
      const v = String(formResp[f.key] ?? "").trim();
      const label = f.label || f.key;
      if (f.required && !v) { toast.error(`${label} is required.`); setLoading(false); return; }
      if (!v) continue;
      if (f.type === "mobile" && !/^[6-9]\d{9}$/.test(v)) { toast.error(`${label}: enter a valid 10-digit mobile number.`); setLoading(false); return; }
      if (f.type === "pan" && !/^[A-Z]{5}[0-9]{4}[A-Z]$/.test(v.toUpperCase())) { toast.error(`${label}: enter a valid PAN (e.g. ABCDE1234F).`); setLoading(false); return; }
      if (f.type === "pincode" && !/^\d{6}$/.test(v)) { toast.error(`${label}: enter a valid 6-digit pincode.`); setLoading(false); return; }
      if (f.type === "email" && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(v)) { toast.error(`${label}: enter a valid email address.`); setLoading(false); return; }
    }
    try {
      const byType = (t) => { const f = fields.find((x) => x.type === t); return f ? String(formResp[f.key] ?? "") : ""; };
      const { data } = await api.post("/apply", {
        ...attribution, campaign_id: code,
        full_name: byType("name"), mobile: byType("mobile"), email: byType("email"), pincode: byType("pincode"),
        responses: formResp, consent,
      });
      if (data.redirect_url) {
        toast.success("Application submitted! Redirecting…");
        window.location.href = data.redirect_url;
      } else {
        setDone(true);
      }
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const renderField = (f) => {
    const val = formResp[f.key] ?? "";
    const set = (v) => setFormResp((p) => ({ ...p, [f.key]: v }));
    const tid = `form-field-${f.key}`;
    if (f.type === "dropdown" || f.type === "gender") {
      const opts = f.type === "gender" ? ["Male", "Female", "Other"] : (f.options || []);
      return <select value={val} onChange={(e) => set(e.target.value)} data-testid={tid} className="mt-1.5 w-full h-11 rounded-md border border-slate-200 px-3 text-sm bg-white"><option value="">Select…</option>{opts.map((o) => <option key={o} value={o}>{o}</option>)}</select>;
    }
    if (f.type === "radio") {
      return <div className="mt-1.5 flex flex-wrap gap-2">{(f.options || []).map((o) => (
        <label key={o} className={`px-3.5 py-1.5 rounded-full border text-xs font-semibold cursor-pointer transition-colors ${val === o ? "bg-[#0B223D] text-white border-[#0B223D]" : "border-slate-200 text-slate-600 hover:border-slate-300"}`}>
          <input type="radio" name={f.key} className="hidden" checked={val === o} onChange={() => set(o)} />{o}
        </label>))}</div>;
    }
    if (f.type === "checkbox") {
      const arr = String(val || "").split(", ").filter(Boolean);
      return (
        <div>
          <Label className="text-sm font-semibold text-slate-600">{f.label} {f.required && <span className="text-rose-500">*</span>}</Label>
          <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-2">{(f.options || []).map((o) => (
            <label key={o} className="flex items-center gap-1.5 text-xs font-semibold text-slate-600">
              <input type="checkbox" checked={arr.includes(o)} onChange={() => set(arr.includes(o) ? arr.filter((x) => x !== o).join(", ") : [...arr, o].join(", "))} data-testid={`${tid}-${o}`} />{o}
            </label>))}</div>
        </div>
      );
    }
    if (f.type === "consent") {
      return <label className="flex items-start gap-2 text-xs text-slate-500"><input type="checkbox" className="mt-0.5" checked={!!val} onChange={(e) => set(e.target.checked ? "yes" : "")} data-testid={tid} /><span>{f.label}</span></label>;
    }
    if (f.type === "mobile") return <Input type="tel" maxLength={10} pattern="[6-9][0-9]{9}" title="10-digit mobile number" value={val} onChange={(e) => set(e.target.value.replace(/\D/g, "").slice(0, 10))} data-testid={tid} className="mt-1.5 h-11" placeholder="10-digit mobile" />;
    if (f.type === "email") return <Input type="email" value={val} onChange={(e) => set(e.target.value)} data-testid={tid} className="mt-1.5 h-11" placeholder="you@example.com" />;
    if (f.type === "dob") return <Input type="date" value={val} onChange={(e) => set(e.target.value)} data-testid={tid} className="mt-1.5 h-11" />;
    if (f.type === "pan") return <Input maxLength={10} pattern="[A-Z]{5}[0-9]{4}[A-Z]" title="PAN format: ABCDE1234F" value={val} onChange={(e) => set(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 10))} data-testid={tid} className="mt-1.5 h-11 uppercase" placeholder="ABCDE1234F" />;
    if (f.type === "pincode") return <Input type="tel" maxLength={6} pattern="[0-9]{6}" title="6-digit pincode" value={val} onChange={(e) => set(e.target.value.replace(/\D/g, "").slice(0, 6))} data-testid={tid} className="mt-1.5 h-11" placeholder="6-digit pincode" />;
    if (f.type === "number" || f.type === "currency") return <Input type="number" value={val} onChange={(e) => set(e.target.value)} data-testid={tid} className="mt-1.5 h-11" />;
    if (f.type === "name") return <Input value={val} onChange={(e) => set(e.target.value.replace(/[^A-Za-z\s.'-]/g, "").slice(0, 60))} data-testid={tid} className="mt-1.5 h-11" placeholder="Your name (letters only)" />;
    return <Input value={val} onChange={(e) => set(e.target.value)} data-testid={tid} className="mt-1.5 h-11" />;
  };

  const checkLead = async () => {
    setCheckRes(null);
    try {
      const { data } = await api.get(`/lead/status?campaign_id=${code}&mobile=${checkMob}`);
      setCheckRes(data);
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  if (!state) return <div className="min-h-screen flex items-center justify-center text-slate-400">Loading…</div>;
  if (!state.available) {
    const paused = state.status === "paused";
    return (
      <div className="min-h-screen bg-[#0B223D] text-white flex items-center justify-center px-6" data-testid="apply-unavailable">
        <div className="text-center max-w-md">
          <Wrench className={`w-12 h-12 mx-auto mb-4 ${paused ? "text-amber-400" : "text-teal-400"}`} />
          <h1 className="font-display text-3xl font-extrabold">{paused ? "Campaign Paused / Hold par hai" : "Campaign currently unavailable."}</h1>
          <p className="text-white/70 mt-3">{state.message || "Please check back later."}</p>
        </div>
      </div>
    );
  }

  const c = state.campaign;
  const dynForm = state.form && state.form.fields?.length ? state.form : null;
  const online = (c.tracking_type || "online") === "online";
  const pinReq = !!c.pincode_required;
  const earnUpto = Math.max(Number(c.payout_pool) || 0, ...(c.payout_slabs || []).map((s) => Number(s.payout) || 0));

  return (
    <div className="min-h-screen bg-slate-50 py-10 px-6" data-testid="apply-campaign-page">
      <div className="max-w-xl mx-auto space-y-5">
        <div className="flex justify-end">
          <select value={lang} onChange={(e) => setLang(e.target.value)} data-testid="apply-lang-select" className="h-9 rounded-lg border border-slate-200 px-2 text-xs font-semibold bg-white text-slate-600">
            {LANGS.map((l) => <option key={l.code} value={l.code}>{l.label}</option>)}
          </select>
        </div>
        {c.desktop_creative && <div className="rounded-3xl overflow-hidden border border-slate-200 shadow-lg bg-white flex items-center justify-center"><img src={c.desktop_creative} alt={c.name} className="w-full h-auto max-h-96 object-contain block" /></div>}
        <div>
          <div className="flex flex-wrap gap-2 mb-2">
            {c.high_payout && <span className="inline-flex items-center gap-1 bg-teal-600 text-white text-xs font-bold px-2.5 py-1 rounded-full" data-testid="badge-high-payout"><Zap className="w-3 h-3" /> High Payout</span>}
            {c.same_day_payout && <span className="inline-flex items-center gap-1 bg-indigo-600 text-white text-xs font-bold px-2.5 py-1 rounded-full" data-testid="badge-same-day"><BadgeCheck className="w-3 h-3" /> Same Day Payout</span>}
            {c.lead_track_percent != null && c.lead_track_percent !== "" && <span className="inline-flex items-center gap-1 bg-sky-600 text-white text-xs font-bold px-2.5 py-1 rounded-full" data-testid="badge-lead-track"><BadgeCheck className="w-3 h-3" /> Lead Track {c.lead_track_percent}%</span>}
            {c.top_converting && <span className="inline-flex items-center gap-1 bg-amber-500 text-white text-xs font-bold px-2.5 py-1 rounded-full" data-testid="badge-top-converting"><Zap className="w-3 h-3" /> Top Converting</span>}
            {earnUpto > 0 && c.payout_type !== "percent" && (
              <span className="relative inline-flex items-center">
                <span className="inline-flex items-center gap-1 bg-teal-600 text-white text-xs font-bold px-2.5 py-1 rounded-full" data-testid="badge-earn-upto">Earn upto {inr(earnUpto)} / lead</span>
                {c.payout_slabs?.length > 0 && (
                  <button type="button" onClick={() => setShowSlab(!showSlab)} data-testid="earn-info-btn" className="ml-1 text-slate-400 hover:text-teal-600 self-center" title="Slab details"><Info className="w-4 h-4" /></button>
                )}
                {showSlab && c.payout_slabs?.length > 0 && (
                  <div className="absolute left-0 top-9 z-20 bg-white border border-slate-200 rounded-xl shadow-lg p-3 w-60 space-y-1" data-testid="earn-slab-panel">
                    <p className="text-[10px] font-bold text-slate-500 uppercase">Slab payout</p>
                    <p className="text-xs text-slate-600">Base: <b className="font-mono-num">{inr(c.payout_pool)}</b> / lead</p>
                    {[...c.payout_slabs].sort((a, b) => a.min_conversions - b.min_conversions).map((s, i) => (
                      <p key={i} className="text-xs text-slate-600">{s.min_conversions}+ leads → <b className="font-mono-num text-teal-700">{inr(s.payout)}</b> / lead</p>
                    ))}
                  </div>
                )}
              </span>
            )}
            <span className="inline-flex items-center gap-1 bg-teal-50 text-teal-700 text-xs font-bold px-2.5 py-1 rounded-full"><span className="w-1.5 h-1.5 rounded-full bg-teal-500 animate-pulse" /> {t("ap_offer_live")}</span>
          </div>
          <h1 className="font-display text-3xl font-extrabold text-[#0B223D]">{c.name}</h1>
          {c.description && <p className="text-slate-500 mt-2">{c.description}</p>}
        </div>

        {done ? (
          <div className="bg-white border border-teal-200 rounded-3xl shadow-sm p-8 text-center" data-testid="apply-success">
            <CheckCircle2 className="w-12 h-12 text-teal-600 mx-auto mb-3" />
            <h3 className="font-display font-bold text-lg text-[#0B223D]">{t("ap_success_title")}</h3>
            <p className="text-sm text-slate-500 mt-1">{t("ap_success_sub")}</p>
          </div>
        ) : dynForm ? (
        <form onSubmit={submitDynamic} className="bg-white border border-slate-200 rounded-3xl shadow-sm p-7 space-y-4" data-testid="apply-dynamic-form">
          <h3 className="font-display font-bold text-lg text-[#0B223D]">{t("ap_apply_now")}</h3>
          <PaymentNote />
          {dynForm.fields.filter((f) => f.type !== "hidden").map((f) => (
            <div key={f.key}>
              {!["checkbox", "consent"].includes(f.type) && <Label className="text-sm font-semibold text-slate-600">{f.label} {f.required && <span className="text-rose-500">*</span>}</Label>}
              {renderField(f)}
            </div>
          ))}
          <label className="flex items-start gap-2 text-xs text-slate-500">
            <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} data-testid="apply-consent" className="mt-0.5" />
            <span>{t("ap_consent")} <a href="/privacy-policy" target="_blank" className="text-teal-600 font-semibold underline">Privacy Policy</a></span>
          </label>
          <Button type="submit" disabled={loading} data-testid="apply-submit-btn" className="w-full bg-[#0B223D] hover:bg-[#06182B] text-white font-bold h-12 rounded-xl disabled:opacity-50">{loading ? t("ap_wait") : <>{t("ap_submit")} <ExternalLink className="w-4 h-4 ml-1.5" /></>}</Button>
        </form>
        ) : (
        <form onSubmit={applyRedirect} className="bg-white border border-slate-200 rounded-3xl shadow-sm p-7 space-y-4" data-testid="apply-form">
          <h3 className="font-display font-bold text-lg text-[#0B223D]">{t("ap_apply_now")}</h3>
          <PaymentNote />
          <div><Label className="text-sm font-semibold text-slate-600">{t("ap_full_name")} <span className="text-rose-500">*</span></Label><Input value={name} required minLength={3} data-testid="apply-full_name" onChange={(e) => setName(e.target.value.replace(/[^A-Za-z\s.'-]/g, "").slice(0, 60))} className="mt-1.5 h-11" placeholder={t("ap_name_ph")} /></div>
          <div><Label className="text-sm font-semibold text-slate-600">{t("ap_mobile")} {online && <span className="text-rose-500">*</span>}</Label><Input type="tel" maxLength={10} pattern="[6-9][0-9]{9}" title="10-digit mobile number" required={online} value={mobile} data-testid="apply-mobile" onChange={(e) => setMobile(e.target.value.replace(/\D/g, "").slice(0, 10))} className="mt-1.5 h-11" placeholder={t("ap_mobile_ph")} /></div>
          <div><Label className="text-sm font-semibold text-slate-600">{t("ap_email")} <span className="text-slate-400 font-normal text-xs">(optional)</span></Label><Input type="email" value={email} data-testid="apply-email" onChange={(e) => setEmail(e.target.value.trim())} className="mt-1.5 h-11" placeholder={t("ap_email_ph")} /></div>
          {pinReq && <div><Label className="text-sm font-semibold text-slate-600">{t("ap_pincode")} <span className="text-rose-500">*</span></Label><Input type="tel" maxLength={6} pattern="[0-9]{6}" title="6-digit pincode" required value={pincode} data-testid="apply-pincode" onChange={(e) => setPincode(e.target.value.replace(/\D/g, "").slice(0, 6))} className="mt-1.5 h-11" placeholder={t("ap_pincode_ph")} /></div>}
          <label className="flex items-start gap-2 text-xs text-slate-500">
            <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} data-testid="apply-consent" className="mt-0.5" />
            <span>{t("ap_consent")} <a href="/privacy-policy" target="_blank" className="text-teal-600 font-semibold underline">Privacy Policy</a></span>
          </label>
          <Button type="submit" disabled={loading} data-testid="apply-submit-btn" className="w-full bg-[#0B223D] hover:bg-[#06182B] text-white font-bold h-12 rounded-xl disabled:opacity-50">{loading ? t("ap_wait") : <>{t("ap_continue")} <ExternalLink className="w-4 h-4 ml-1.5" /></>}</Button>
          <p className="text-xs text-slate-400 text-center">{t("ap_redirect_note")}</p>
        </form>
        )}

        {online && (
          <div className="bg-white border border-slate-200 rounded-3xl p-7 space-y-3" data-testid="lead-status-checker">
            <h3 className="font-display font-bold text-lg text-[#0B223D] flex items-center gap-2"><Search className="w-5 h-5 text-teal-500" /> {t("ap_checker_title")}</h3>
            <p className="text-sm text-slate-500">{t("ap_checker_sub")}</p>
            <div className="flex gap-2">
              <Input type="tel" maxLength={10} value={checkMob} data-testid="lead-check-mobile" onChange={(e) => setCheckMob(e.target.value.replace(/\D/g, "").slice(0, 10))} className="h-11" placeholder={t("ap_checker_ph")} />
              <Button onClick={checkLead} data-testid="lead-check-btn" className="bg-teal-600 hover:bg-teal-700 text-white font-bold h-11 px-5">{t("ap_check_btn")}</Button>
            </div>
            {checkRes && (() => {
              const tone = checkRes.tracked ? (checkRes.tone || "info") : "warn";
              const toneCls = {
                success: "bg-teal-50 border-teal-200",
                waiting: "bg-sky-50 border-sky-200",
                error: "bg-rose-50 border-rose-200",
                info: "bg-slate-50 border-slate-200",
                warn: "bg-amber-50 border-amber-200",
              }[tone];
              const iconCls = { success: "text-teal-600", waiting: "text-sky-600", error: "text-rose-600", info: "text-slate-500", warn: "text-amber-600" }[tone];
              const steps = ["Submitted", "Waiting for brand report", "Approved", "Paid"];
              const stageIdx = { submitted: 1, processing: 1, approved: 2, disbursed: 3 }[checkRes.status] ?? 0;
              return (
                <div className={`rounded-xl p-4 border ${toneCls}`} data-testid="lead-check-result">
                  <div className="flex items-start gap-2.5">
                    {checkRes.tracked
                      ? (tone === "success" ? <CheckCircle2 className={`w-6 h-6 shrink-0 ${iconCls}`} /> : tone === "waiting" ? <Clock3 className={`w-6 h-6 shrink-0 ${iconCls} animate-pulse`} /> : tone === "error" ? <XCircle className={`w-6 h-6 shrink-0 ${iconCls}`} /> : <Search className={`w-6 h-6 shrink-0 ${iconCls}`} />)
                      : <Search className="w-6 h-6 text-amber-600 shrink-0" />}
                    <div className="min-w-0">
                      <p className="font-bold text-[#0B223D]">{checkRes.tracked ? checkRes.status_label : "Not tracked yet"}</p>
                      <p className="text-sm text-slate-600 mt-0.5">{checkRes.message}</p>
                    </div>
                  </div>
                  {checkRes.tracked && checkRes.status !== "rejected" && (
                    <div className="mt-3.5 flex items-center" data-testid="lead-progress-tracker">
                      {steps.map((s, i) => (
                        <React.Fragment key={s}>
                          <div className="flex flex-col items-center gap-1 shrink-0">
                            <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-bold ${i < stageIdx ? "bg-teal-500 text-white" : i === stageIdx ? "bg-[#0B223D] text-white ring-2 ring-[#0B223D]/30" : "bg-white border border-slate-300 text-slate-400"}`}>{i < stageIdx ? "✓" : i + 1}</span>
                            <span className={`text-[9px] font-semibold text-center leading-tight w-14 ${i === stageIdx ? "text-[#0B223D]" : "text-slate-400"}`}>{s}</span>
                          </div>
                          {i < steps.length - 1 && <div className={`flex-1 h-0.5 mx-1 mb-4 ${i < stageIdx ? "bg-teal-400" : "bg-slate-200"}`} />}
                        </React.Fragment>
                      ))}
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        )}
      </div>
    </div>
  );
}
